import lodashIsEqual from 'lodash.isequal';
import memoize from 'micro-memoize';
import type { DateTime } from './dates';
import type { DeepPartial, LooseObject, NestedArray } from './types';
export { memoize };

type SortSpec<T> = {
  fn: (item: T) => string | number | boolean | Date | null | undefined;
  order: 'asc' | 'desc';
};

export const isEqual = (value: unknown, other: unknown): boolean => {
  return lodashIsEqual(value, other);
};

export const run = <T>(f: () => T): T => {
  return f();
};

export const get = <K extends PropertyKey, V>(
  value: Record<K, V>,
  path: string,
  defaultValue: V | null = null,
): V | null => {
  const segments = path.split(/[.[\]]/g);
  let current: any = value;

  for (const key of segments) {
    if (current === null) {
      return defaultValue;
    }

    if (current === undefined) {
      return defaultValue;
    }

    if (key.trim() === '') {
      continue;
    }

    current = current[key];
  }

  if (current === undefined) {
    return defaultValue;
  }

  return current;
};

export const isNil = (value: unknown): value is null | undefined => {
  return value === null || value === undefined;
};

export const isNull = (value: unknown): value is null => {
  return value === null;
};

export const isUndefined = (value: unknown): value is undefined => {
  return value === undefined;
};

export const isNumber = (value: unknown): value is number => {
  try {
    return Number(value) === value;
  } catch {
    return false;
  }
};

export const isNumberFinite = (v: number): boolean => {
  return Number.isFinite(v);
};

export const isString = (value: unknown): value is string => {
  return typeof value === 'string' || value instanceof String;
};

export const isEmptyString = (value: string): boolean => {
  return value.length === 0;
};

export const isBoolean = (value: unknown): value is boolean => {
  return value === !!value;
};

export const isArray = <T>(value: unknown): value is T[] => {
  return Array.isArray(value);
};

export const isObject = (value: unknown): value is object => {
  return typeof value === 'object' && !isNull(value) && value.constructor === Object;
};

export const isEmptyObject = <T extends object>(value: T): boolean => {
  return isObject(value) && isEmpty(Object.keys(value));
};

export const isPlainObject = (value: unknown): value is object => {
  return (
    typeof value === 'object' &&
    !isNull(value) &&
    value.constructor === Object &&
    Object.getPrototypeOf(value) === Object.prototype
  );
};

export const isStrictNaN = (value: number): boolean => {
  return Number.isNaN(value);
};

export const parseNumeric = (value: unknown): number | undefined => {
  const parsedValue = Number(value);
  return isStrictNaN(parsedValue) ? undefined : parsedValue;
};

export const range = (start: number, end: number): number[] => {
  const result = [];
  for (let i = start; i < end; i++) {
    result.push(i);
  }
  return result;
};

export const chunk = <T>(list: T[], size: number): T[][] => {
  const chunkCount = Math.ceil(list.length / size);
  return new Array(chunkCount).fill(null).map((_c: null, i: number) => {
    return list.slice(i * size, i * size + size);
  });
};

export const first = <T>(array: T[], defaultValue: T | null = null): T | null => {
  return array.at(0) ?? defaultValue;
};

export const last = <T>(array: T[], defaultValue: T | null = null): T | null => {
  return array.at(array.length - 1) ?? defaultValue;
};

export const take = <T>(array: T[], count: number): T[] => {
  return array.slice(0, count);
};

export const takeRight = <T>(array: T[], count: number): T[] => {
  return array.slice(-count);
};

export const isEmpty = <T>(array: T[]): boolean => {
  return array.length === 0;
};

export const mapValues = <TValue, TKey extends string, TNewValue>(
  obj: Record<TKey, TValue>,
  mapFunc: (value: TValue, key: TKey) => TNewValue,
): Record<TKey, TNewValue> => {
  const keys = Object.keys(obj) as TKey[];
  return keys.reduce(
    (acc, key) => {
      acc[key] = mapFunc(obj[key], key);
      return acc;
    },
    {} as Record<TKey, TNewValue>,
  );
};

export const orderBy = <
  T,
  V extends string | number | boolean | Date | DateTime | undefined | null,
>(
  array: T[],
  predicate: (item: T) => V,
  order: 'asc' | 'desc' = 'asc',
): T[] => {
  const compare = (a: T, b: T): number => {
    const valueA = predicate(a) ?? false;
    const valueB = predicate(b) ?? false;

    if (valueA < valueB) {
      return order === 'asc' ? -1 : 1;
    }
    if (valueA > valueB) {
      return order === 'asc' ? 1 : -1;
    }
    return 0;
  };

  return array.slice().sort(compare);
};

export const orderByMultiple = <T>(array: T[], specs: SortSpec<T> | SortSpec<T>[]): T[] => {
  const sortSpecs = Array.isArray(specs) ? specs : [specs];

  const compare = (a: T, b: T): number => {
    for (const { fn, order } of sortSpecs) {
      const valA = fn(a) ?? false;
      const valB = fn(b) ?? false;

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
    }
    return 0;
  };

  return array.slice().sort(compare);
};

export const groupBy = <T, Key extends string | number | symbol>(
  array: T[],
  fn: (item: T) => Key,
): Record<Key, T[] | undefined> => {
  return array.reduce(
    (acc, item) => {
      const groupId = fn(item);
      if (!acc[groupId]) acc[groupId] = [];
      acc[groupId].push(item);
      return acc;
    },
    {} as Record<Key, T[] | undefined>,
  );
};

//  TODO: look into creating a strictPickBy or modifying pickBy to avoid errors due to Partial
export const pickBy = <T extends LooseObject>(
  obj: T,
  predicate: (value: T[keyof T], key: keyof T) => boolean,
): Partial<T> => {
  return Object.keys(obj).reduce(
    (result, key) => {
      const typedKey = key as keyof T;
      const value = obj[typedKey];

      if (predicate(value, typedKey)) {
        result[typedKey] = value;
      }

      return result;
    },
    {} as Partial<T>,
  );
};

export const compact = <A>(array: (A | undefined | null)[]): A[] => {
  return array.filter((v): v is A => v !== undefined && v !== null);
};

export const flatten = <T>(arr: NestedArray<T>, result: T[] = []): T[] => {
  for (const value of arr) {
    if (Array.isArray(value)) {
      flatten<T>(value, result);
    } else {
      result.push(value);
    }
  }

  return result;
};

export const difference = <T>(array: T[], values: T[]): T[] => {
  return array.filter((x) => !values.includes(x));
};

export const differenceBy = <T, K>(array1: T[], array2: T[], predicate: (item: T) => K): T[] => {
  const map = new Map<K, T>();

  array2.forEach((item) => {
    const key = predicate(item);
    map.set(key, item);
  });

  return array1.filter((item) => {
    const key = predicate(item);
    return !map.has(key);
  });
};

export const differenceWith = <T>(
  array: T[],
  values: T[],
  comparator: (a: T, b: T) => boolean,
): T[] => {
  return array.filter((a) => !values.some((b) => comparator(a, b)));
};

export const intersect = <T>(array: T[], values: T[]): T[] => {
  const set = new Set(values);
  return array.filter((item) => set.has(item));
};

export const intersectBy = <T, U>(array: T[], values: T[], iteratee: (item: T) => U): T[] => {
  const set = new Set(values.map(iteratee));
  return array.filter((item) => set.has(iteratee(item)));
};

export const omit = <T, TKeys extends keyof T>(obj: T, keys: TKeys[]): Omit<T, TKeys> => {
  if (!obj) {
    return {} as Omit<T, TKeys>;
  }

  if (!keys || keys.length === 0) {
    return obj as Omit<T, TKeys>;
  }

  return keys.reduce(
    (acc, key) => {
      delete acc[key];
      return acc;
    },
    { ...obj },
  );
};

export const omitBy = <T>(
  obj: Record<string, T>,
  predicate: (value: T, key: string) => boolean,
): Record<string, T> => {
  return Object.fromEntries(Object.entries(obj).filter(([key, value]) => !predicate(value, key)));
};

export const uniq = <T>(array: T[]): T[] => {
  return [...new Set(array)];
};

export const uniqBy = <T extends object, K extends PropertyKey>(
  array: T[],
  keyFn: (item: T) => K,
): T[] => {
  const valueMap = array.reduce(
    (acc, item) => {
      const key = keyFn(item);
      if (acc[key]) return acc;
      acc[key] = item;
      return acc;
    },
    {} as Record<K, T>,
  );
  return Object.values(valueMap);
};

export const uniqWith = <T>(array: T[], comparator: (a: T, b: T) => boolean): T[] => {
  return array.reduce<T[]>((acc, item) => {
    if (!acc.some((existingItem) => comparator(existingItem, item))) {
      acc.push(item);
    }
    return acc;
  }, []);
};

export const minBy = <T>(array: T[], fn: (item: T) => number): T | undefined => {
  let result = array[0];

  if (!result) {
    return undefined;
  }

  let min = fn(result);

  for (let i = 1; i < array.length; i++) {
    const item = array[i];

    if (!item) {
      continue;
    }

    const value = fn(item);

    if (value < min) {
      min = value;
      result = array[i];
    }
  }
  return result;
};

export const maxBy = <T>(array: T[], fn: (item: T) => number): T | undefined => {
  let result = array[0];

  if (!result) {
    return undefined;
  }

  let max = fn(result);

  for (let i = 1; i < array.length; i++) {
    const item = array[i];

    if (!item) {
      continue;
    }

    const value = fn(item);

    if (value > max) {
      max = value;
      result = array[i];
    }
  }
  return result;
};

export const keyBy = <T extends object, K extends PropertyKey>(
  array: T[],
  keyFn: (item: T) => K,
): { [P in K]: T } => {
  return array.reduce(
    (r, x) => {
      r[keyFn(x)] = x;
      return r;
    },
    {} as { [P in K]: T },
  );
};

export const listify = <TValue, TKey extends PropertyKey, TNewValue>(
  obj: Record<TKey, TValue>,
  mapFn: (value: TValue, key: TKey) => TNewValue,
): TNewValue[] => {
  return Object.entries(obj).reduce<TNewValue[]>((acc, [key, value]) => {
    acc.push(mapFn(value as TValue, key as TKey));
    return acc;
  }, []);
};

export const identity = <T>(value: T): T => {
  return value;
};

export const constant = <T>(value: T) => {
  return () => value;
};

export const noop = (): void => {
  return;
};

export const round = (number: number, precision = 0) => {
  const factor = 10 ** precision;
  return (Math.sign(number) * Math.round(Math.abs(number * factor))) / factor;
};

export const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

export const capitalize = (value: string) => {
  return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
};

export const toSentence = (arr: string[]) => {
  return (
    arr.slice(0, -2).join(', ') +
    (arr.slice(0, -2).length ? ', ' : '') +
    arr.slice(-2).join(' and ')
  );
};

export const randomString = (length: number) => {
  return Math.random().toString(36).slice(2).slice(0, length);
};

export const randomNumber = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const intersperse = <T>(arr: T[], separator: (n: number) => T): T[] => {
  return arr.reduce<T[]>((acc, currentElement, currentIndex) => {
    acc.push(currentElement);
    if (currentIndex !== arr.length - 1) {
      acc.push(separator(currentIndex));
    }
    return acc;
  }, []);
};

export const times = (count: number, f: (index: number) => void) => {
  for (let i = 0; i < count; i++) {
    f(i);
  }
};

export const shuffle = <T>(array: T[]) => {
  return array
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
};

export const until = <A>(f: () => A, pred: (value: A) => boolean): A => {
  let value = f();
  while (!pred(value)) {
    value = f();
  }
  return value;
};

export const moveArrayItem = <T>(array: T[], fromIndex: number, toIndex: number) => {
  if (fromIndex < 0 || fromIndex > array.length) {
    throw new Error(`fromIndex must be between 0 and array length, you provided ${fromIndex}`);
  }

  if (toIndex < 0 || toIndex > array.length) {
    throw new Error(`toIndex must be between 0 and array length, you provided ${toIndex}`);
  }

  const clone = [...array];
  clone.splice(toIndex, 0, clone.splice(fromIndex, 1)[0]!);
  return clone;
};

export const zeroPad = (num: number, places: number) => {
  return String(num).padStart(places, '0');
};

export const upperFirst = (str: string): string => {
  if (str.length === 0) {
    return '';
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const zip = <T>(...arrays: T[][]): T[][] => {
  const maxLength = Math.max(...arrays.map((arr) => arr.length));
  return Array.from({ length: maxLength }, (_, index) => arrays.map((arr) => arr[index]!));
};

export const sum = (array: number[]): number => {
  let total = 0;

  for (const value of array) {
    total += value;
  }

  return total;
};

export const sumBy = <T>(array: T[], iteratee: (item: T) => number): number => {
  let total = 0;

  for (const value of array) {
    total += iteratee(value);
  }

  return total;
};

export const countBy = <T>(array: T[], iteratee: (item: T) => string): Record<string, number> => {
  return array.reduce<Record<string, number>>((acc, item) => {
    const key = iteratee(item);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
};

// https://github.com/zebateira/node-deep-trim/blob/main/index.js
export const deepTrim = (object: any): any => {
  if (typeof object === 'string') {
    return object.trim();
  } else if (typeof object === 'object') {
    for (const key in object) {
      object[key] = deepTrim(object[key]);
    }
  }

  return object;
};

export const deepObjectDiff = <A extends PropertyKey>(
  before: Partial<Record<A, any>>,
  after: Partial<Record<A, any>>,
): Partial<Record<A, any>> => {
  return pickBy(after, (value, key) => {
    return !isEqual(value, before[key]);
  });
};

export const trimWhitespace = (value: string): string => {
  return value.replace(/(\s|&nbsp;)+/g, ' ').trim();
};

export const trimStart = (value: string, chars = '\\s'): string => {
  return value.replace(new RegExp(`^([${chars}]*)(.*)$`), '$2');
};

export const trimEnd = (value: string, chars = '\\s') => {
  return value.replace(new RegExp(`^(.*?)([${chars}]*)$`), '$1');
};

export const deepDefaults = <T extends LooseObject>(
  defaults: T,
  overrides: DeepPartial<T> = {},
): T => {
  const merge = (overrideValue: any, defaultValue: any): any => {
    if (overrideValue === undefined) {
      return defaultValue;
    }

    if (!isObject(defaultValue) || Object.keys(defaultValue).length === 0) {
      return overrideValue;
    }

    const defaultObj = defaultValue as LooseObject;

    for (const key in defaultValue) {
      const current = defaultObj[key];
      const override = overrideValue[key];
      defaultObj[key] = merge(override, current);
    }

    return defaultObj;
  };

  return merge(overrides, defaults);
};

export const unionBy = <T>(array1: T[], array2: T[], iteratee: (item: T) => any): T[] => {
  const map = new Map<any, T>();

  array1.forEach((item) => {
    const key = iteratee(item);
    map.set(key, item);
  });

  array2.forEach((item) => {
    const key = iteratee(item);
    if (!map.has(key)) {
      map.set(key, item);
    }
  });

  return Array.from(map.values());
};

export const sample = <A = unknown>(array: A[]): A => {
  if (array.length === 0) {
    throw new Error('Array is empty');
  }

  return array[Math.floor(Math.random() * array.length)]!;
};

export const partition = <T>(array: T[], predicate: (item: T) => boolean): [T[], T[]] => {
  return array.reduce<[T[], T[]]>(
    (acc, item) => {
      if (predicate(item)) {
        acc[0].push(item);
      } else {
        acc[1].push(item);
      }
      return acc;
    },
    [[], []],
  );
};

export const cloneDeep = <T>(value: T): T => {
  return structuredClone(value);
};

export const slugify = (text: string): string => {
  return text
    .replace(/([a-z])([A-Z])/g, '$1-$2') // insert dash between camelCase words
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // replace non-alphanumeric chars with dashes
    .replace(/^-+|-+$/g, '') // trim leading and trailing dashes
    .replace(/--+/g, '-'); // collapse multiple dashes
};
